import React from "react";
import {
  Sandwich, ShieldCheck, AlertTriangle, ShieldX, HelpCircle, Info,
  Zap, Coins,
} from "lucide-react";
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent,
} from "./ui/card";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";

/**
 * MsisCard — MEV Sandwich Intensity Score (F4).
 *
 * Surfaces the MSIS modifier from /api/scan:
 *   report.risk_modifiers.msis = {
 *     enabled, ok, band: "PASS"|"WARN"|"FAIL"|null,
 *     penalty, skipped_reason,
 *     details: {
 *       sandwiched_volume_pct, sandwiched_sol, total_sol, incidents,
 *       thresholds: { pct_warn, pct_fail }  (optional)
 *     }
 *   }
 *
 * Also surfaces the MSIS × VA combined-cap decision when both fired:
 *   report.risk = {
 *     msis_va_combined_cap_applied: bool,
 *     msis_va_combined_cap_clawback: int,
 *   }
 */

function bandMeta(band) {
  switch (band) {
    case "PASS":
      return { label: "Clean swap path", variant: "success", icon: ShieldCheck };
    case "WARN":
      return { label: "Sandwich activity", variant: "warning", icon: AlertTriangle };
    case "FAIL":
      return { label: "Heavy sandwiching", variant: "danger", icon: ShieldX };
    default:
      return { label: "Not scored", variant: "muted", icon: HelpCircle };
  }
}

function humanizeSkippedReason(reason) {
  if (!reason) return "";
  if (reason === "disabled") return "MSIS is disabled on the backend.";
  if (reason === "no_swaps") return "No swaps available for sandwich-pattern analysis.";
  if (reason === "no_helius_key") return "HELIUS_API_KEY missing on the backend.";
  if (reason.startsWith("rpc_error:")) return `RPC error: ${reason.slice("rpc_error:".length)}`;
  return reason;
}

export function MsisCard({ msis, risk }) {
  if (!msis) return null;
  const meta = bandMeta(msis.band);
  const Icon = meta.icon;
  const d = msis.details || {};
  const isScored = msis.band === "PASS" || msis.band === "WARN" || msis.band === "FAIL";
  const reasonText = humanizeSkippedReason(msis.skipped_reason);

  const pct = Number(d.sandwiched_volume_pct ?? 0);
  const sandwichedSol = Number(d.sandwiched_sol ?? 0);
  const totalSol = Number(d.total_sol ?? 0);
  const incidents = Number(d.incidents ?? 0);

  const capApplied = !!risk?.msis_va_combined_cap_applied;
  const clawback = Number(risk?.msis_va_combined_cap_clawback ?? 0);

  // Colour for the headline pct
  const pctColor =
    pct >= 40 ? "text-[hsl(var(--danger))]"
      : pct >= 15 ? "text-[hsl(var(--warning))]"
        : "text-[hsl(var(--success))]";
  const barWidth = `${Math.max(0, Math.min(100, pct))}%`;
  const barColor =
    pct >= 40 ? "bg-[hsl(var(--danger))]"
      : pct >= 15 ? "bg-[hsl(var(--warning))]"
        : "bg-[hsl(var(--success))]";

  return (
    <Card className="p-0" data-testid="msis-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <Sandwich size={16} className="mt-0.5 text-[hsl(var(--muted))]" />
            <div>
              <CardTitle>MEV Sandwich Intensity</CardTitle>
              <CardDescription>
                Share of swap volume that flowed through detected
                attacker-buy → victim → attacker-sell triplets.
              </CardDescription>
            </div>
          </div>
          <Badge variant={meta.variant} className="gap-1">
            <Icon size={12} /> {meta.label}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {!isScored ? (
          <div className="rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev-2))] px-3 py-3">
            <div className="flex items-start gap-2 text-xs text-[hsl(var(--muted))]">
              <Info size={13} className="mt-0.5 shrink-0" />
              <span>{reasonText || "MSIS could not score this token."}</span>
            </div>
          </div>
        ) : (
          <>
            {/* Headline pct + bar */}
            <div className="rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev-2))] px-3 py-3">
              <div className="flex items-baseline justify-between gap-3">
                <div className="flex items-baseline gap-2">
                  <span
                    className={`text-2xl font-semibold tabular-nums ${pctColor}`}
                    data-testid="msis-sandwiched-pct"
                  >
                    {pct.toFixed(1)}%
                  </span>
                  <span className="text-xs text-[hsl(var(--muted))]">
                    of swap volume sandwiched
                  </span>
                </div>
                <span className="text-[11px] text-[hsl(var(--muted))] font-mono">
                  {incidents} incident{incidents === 1 ? "" : "s"}
                </span>
              </div>
              <div className="h-1.5 mt-2 rounded-full bg-[hsl(var(--bg-elev-3))] overflow-hidden">
                <div
                  className={`h-full ${barColor} transition-all`}
                  style={{ width: barWidth }}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div
                className="px-3 py-2 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev-2))]"
                data-testid="msis-sandwiched-sol"
              >
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted))]">
                  <Zap size={11} /> Sandwiched SOL
                </div>
                <div className="mt-1 font-mono text-base tabular-nums">
                  {sandwichedSol.toFixed(3)}
                </div>
              </div>
              <div
                className="px-3 py-2 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev-2))]"
                data-testid="msis-total-sol"
              >
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted))]">
                  <Coins size={11} /> Total inspected
                </div>
                <div className="mt-1 font-mono text-base tabular-nums">
                  {totalSol.toFixed(3)} SOL
                </div>
              </div>
            </div>

            {msis.penalty ? (
              <>
                <Separator />
                <div className="text-[11px] text-[hsl(var(--muted))]">
                  Score penalty applied:{" "}
                  <span className="font-mono text-[hsl(var(--warning))]">
                    −{msis.penalty}
                  </span>
                </div>
              </>
            ) : null}

            {capApplied ? (
              <div
                className="rounded-md border border-[hsl(var(--warning)/0.4)] bg-[hsl(var(--warning)/0.06)] px-3 py-2"
                data-testid="msis-va-combined-cap"
              >
                <div className="flex items-start gap-2 text-[11px] text-[hsl(var(--warning))]">
                  <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                  <span>
                    MSIS × Volume-authenticity combined cap applied (+18 max joint penalty).
                    Combined deduction clawed back by{" "}
                    <span className="font-mono">−{clawback}</span> points to avoid
                    double-counting the same wash operation.
                  </span>
                </div>
              </div>
            ) : null}
          </>
        )}

        <p className="text-[10px] text-[hsl(var(--muted))]">
          Bands key on <span className="font-mono">sandwiched_volume_pct</span>:
          ≥40% FAIL · 15–39% WARN · &lt;15% PASS.
          A sandwich is a triplet where an attacker brackets a victim swap in the same slot.
        </p>
      </CardContent>
    </Card>
  );
}

export default MsisCard;
